import { Controller } from "@hotwired/stimulus"

/**
* Stimulus controller for managing saved form view list interactions
* Handles loading and deleting saved searches with dropdown management
*
* Usage:
* <div data-controller="saved-form-view--list"
*      data-saved-form-view--list-url-value="/saved_searches"
*      data-saved-form-view--list-context-value="portfolio">
*   <button data-action="saved-form-view--list#loadSearch" data-search-id="123">Load</button>
*   <button data-action="saved-form-view--list#deleteSearch" data-search-id="123">Delete</button>
* </div>
*/
export default class extends Controller {
  static values = { 
    context: String,
    url: String,
    formSelector: String
  }

  // Capture clicks so we can do pointer interactions
  stopPropagation(event) {
    event.stopPropagation()
  }

  loadSearch(event) {
    event.preventDefault()
    event.stopPropagation()
    const searchId = event.currentTarget.dataset.searchId
    fetch(`${this.urlValue}/${searchId}`)
      .then(response => {
        if (response.redirected) {
          // Probably a session timeout
          return window.location.reload()
        } else {
          if (!response.ok) {
            if (response.headers.get('Content-Type')?.includes('turbo-stream')) {
              response.text().then(html => Turbo.renderStreamMessage(html))
              return
            } else {
              return window.location.reload()
            }
          }
        }
        return response.json()
      })
      .then(data => {
        if (!data) return // Response was handled by Turbo

        document.dispatchEvent(new CustomEvent('saved-form-view:load', {
          detail: {
            searchParams: data.search_params,
            searchId: searchId,
            searchName: data.name
          },
          bubbles: true
        }))
        this.closeDropdown(event.target)
      })
      .catch(error => {
        console.error('Failed to load search:', error)
      })
  }

  deleteSearch(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!confirm('Are you sure you want to delete this saved view?')) return
    
    const searchId = event.currentTarget.dataset.searchId
    
    fetch(`${this.urlValue}/${searchId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.text() // Get the HTML content
      } else {
        throw new Error('Delete failed')
      }
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
      this.closeDropdown(event.target)
    })
    .catch(error => {
      console.error('Delete error:', error)
    })
  }

  closeDropdown(element) {
    const dropdown = element.closest('.dropdown')
    if (dropdown) {
      const dropdownToggle = dropdown.querySelector('[data-bs-toggle="dropdown"]')
      if (dropdownToggle && window.bootstrap) {
        const bsDropdown = window.bootstrap.Dropdown.getInstance(dropdownToggle)
        if (bsDropdown) bsDropdown.hide()
      }
    }
  }
}
